import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '../ui/Button';
import Input from '../ui/Input';
import DatePicker from '../ui/DatePicker';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import Can from '../../auth/Can';
import FieldAnswerList from '../fields/FieldAnswerList';
import { updateCandidate } from '../../api/candidates';
import { listNationalities } from '../../api/masters';
import { pushToast } from '../ui/toastBus';
import ProfilePhoto from './ProfilePhoto';

const FIELD_LABELS = {
  email: 'Email',
  contact_no: 'Contact No',
  whatsapp_no: 'WhatsApp No',
  home_country_contact_no: 'Home Country Contact',
  nationality: 'Nationality',
  date_of_birth: 'Date of Birth',
  passport_no: 'Passport No',
  passport_expiry_date: 'Passport Expiry',
  old_emirates_id_no: 'Old Emirates ID No',
  referred_agent_name: "Referred Agent's Name",
  visa_type: 'Visa Type',
  visa_validity: 'Visa Validity',
  uid_no: 'UID No',
  uae_license_no: 'UAE DL No',
  uae_license_expiry: 'UAE DL Expiry',
  home_license_no: 'HC (Home Country) DL No',
  home_license_expiry: 'HC (Home Country) DL Expiry',
};

function formatAnswer(value) {
  return Array.isArray(value) ? value.join(', ') : String(value);
}

function ReadOnlySheet({ candidate, nationalityFields }) {
  const fields = Object.keys(FIELD_LABELS).filter((key) => candidate[key]);
  const answers = candidate.nationality_fields || {};

  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
      <div className="col-span-2">
        <ProfilePhoto kind="candidates" id={candidate.id} hasPhoto={candidate.has_profile_photo} name={candidate.full_name} size="h-24 w-20" />
      </div>
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Name</dt>
        <dd className="text-sm text-gray-800">{candidate.full_name}</dd>
      </div>
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Reference No</dt>
        <dd className="text-sm text-gray-800">{candidate.reference_no}</dd>
      </div>
      {fields.map((key) => (
        <div key={key}>
          <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">{FIELD_LABELS[key]}</dt>
          <dd className="text-sm text-gray-800">{String(candidate[key])}</dd>
        </div>
      ))}
      {nationalityFields
        .filter((f) => answers[f.id] !== undefined && answers[f.id] !== '' && answers[f.id]?.length !== 0)
        .map((f) => (
          <div key={f.id}>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">{f.label}</dt>
            <dd className="text-sm text-gray-800">{formatAnswer(answers[f.id])}</dd>
          </div>
        ))}
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">RTA Permit</dt>
        <dd className="text-sm text-gray-800">{candidate.has_rta_permit ? 'Yes' : 'No'}</dd>
      </div>
      {candidate.has_rta_permit && candidate.rta_permit_details && (
        <div className="col-span-2">
          <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">RTA Permit Details</dt>
          <dd className="text-sm text-gray-800">{candidate.rta_permit_details}</dd>
        </div>
      )}
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Visited UAE Before</dt>
        <dd className="text-sm text-gray-800">{candidate.visited_uae_before ? 'Yes' : 'No'}</dd>
      </div>
      {candidate.visited_uae_before && (
        <>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Old Visa</dt>
            <dd className="text-sm text-gray-800">{candidate.old_visa || '—'}</dd>
          </div>
          {candidate.old_visa_remark && (
            <div className="col-span-2">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Old Visa Remark</dt>
              <dd className="text-sm text-gray-800">{candidate.old_visa_remark}</dd>
            </div>
          )}
        </>
      )}
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Source</dt>
        <dd className="text-sm text-gray-800">{candidate.source || '—'}</dd>
      </div>
    </dl>
  );
}

export default function CandidateProfileTab({ candidate }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => ({ ...candidate }));
  const [errors, setErrors] = useState({});

  const nationalitiesQuery = useQuery({ queryKey: ['nationalities'], queryFn: () => listNationalities() });
  const nationalities = nationalitiesQuery.data || [];
  const activeNationalityId = editing ? form.nationality_id : candidate.nationality_id;
  const nationalityFields = nationalities.find((n) => String(n.id) === String(activeNationalityId))?.fields || [];

  const mutation = useMutation({
    mutationFn: (payload) => updateCandidate(candidate.id, payload),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Candidate updated.' });
      queryClient.invalidateQueries({ queryKey: ['candidates', String(candidate.id)] });
      setEditing(false);
      setErrors({});
    },
    onError: (err) => setErrors(err.response?.data?.errors || {}),
  });

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const setNationality = (e) => setForm((f) => ({ ...f, nationality_id: e.target.value, nationality_fields: {} }));

  const setNationalityAnswer = (fieldId, value) =>
    setForm((f) => ({ ...f, nationality_fields: { ...(f.nationality_fields || {}), [fieldId]: value } }));

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate(form);
  };

  const fieldError = (field) => errors[field]?.[0];

  if (!editing) {
    return (
      <div>
        <div className="mb-4 flex justify-end">
          <Can perm="candidates.update">
            <Button
              variant="secondary"
              onClick={() => {
                setForm({ ...candidate });
                setEditing(true);
              }}
            >
              Edit profile
            </Button>
          </Can>
        </div>
        <ReadOnlySheet candidate={candidate} nationalityFields={nationalityFields} />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input label="Full Name" value={form.full_name} onChange={set('full_name')} error={fieldError('full_name')} className="col-span-2" />
        <Input label="Email" type="email" value={form.email || ''} onChange={set('email')} error={fieldError('email')} />
        <Input label="Contact No" value={form.contact_no} onChange={set('contact_no')} error={fieldError('contact_no')} />
        <Input label="WhatsApp No" value={form.whatsapp_no || ''} onChange={set('whatsapp_no')} />
        <Input label="Home Country Contact" value={form.home_country_contact_no || ''} onChange={set('home_country_contact_no')} />
        <Select label="Nationality" value={form.nationality_id || ''} onChange={setNationality} error={fieldError('nationality_id')}>
          <option value="">Select…</option>
          {nationalities.map((n) => (
            <option key={n.id} value={n.id}>
              {n.name}
            </option>
          ))}
        </Select>
        <DatePicker label="Date of Birth" value={form.date_of_birth?.slice(0, 10)} onChange={set('date_of_birth')} />
        <Input label="Passport No" value={form.passport_no} onChange={set('passport_no')} error={fieldError('passport_no')} />
        <DatePicker label="Passport Expiry" value={form.passport_expiry_date?.slice(0, 10)} onChange={set('passport_expiry_date')} error={fieldError('passport_expiry_date')} />
        <Input label="Old Emirates ID No" value={form.old_emirates_id_no || ''} onChange={set('old_emirates_id_no')} />
        <Input label="Referred Agent's Name" value={form.referred_agent_name || ''} onChange={set('referred_agent_name')} />
      </div>

      {nationalityFields.length > 0 && (
        <FieldAnswerList
          fields={nationalityFields}
          answers={form.nationality_fields || {}}
          onAnswer={setNationalityAnswer}
          errorFor={(id) => fieldError(`nationality_fields.${id}`)}
          className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-4"
        />
      )}

      {candidate.visa_type !== undefined && candidate.category.location === 'inside_uae' && (
        <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-4">
          <Select label="Visa Type" value={form.visa_type || ''} onChange={set('visa_type')}>
            <option value="">Select…</option>
            <option value="visit">Visit</option>
            <option value="employment">Employment</option>
          </Select>
          <DatePicker label="Visa Validity" value={form.visa_validity?.slice(0, 10) || ''} onChange={set('visa_validity')} />
          <Input label="UID No" value={form.uid_no || ''} onChange={set('uid_no')} />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-4">
        {candidate.category.license_type === 'uae' && (
          <>
            <Input label="UAE DL No" value={form.uae_license_no || ''} onChange={set('uae_license_no')} />
            <DatePicker label="UAE DL Expiry" value={form.uae_license_expiry?.slice(0, 10) || ''} onChange={set('uae_license_expiry')} />
          </>
        )}
        {candidate.category.license_type === 'gcc_home' && (
          <>
            <Input label="HC (Home Country) DL No" value={form.home_license_no || ''} onChange={set('home_license_no')} />
            <DatePicker label="HC (Home Country) DL Expiry" value={form.home_license_expiry?.slice(0, 10) || ''} onChange={set('home_license_expiry')} />
          </>
        )}
      </div>

      <div className="border-t border-gray-100 pt-4">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input type="checkbox" checked={!!form.has_rta_permit} onChange={set('has_rta_permit')} />
          Holds an RTA permit with Dubai companies?
        </label>
        {form.has_rta_permit && (
          <Textarea className="mt-2" value={form.rta_permit_details || ''} onChange={set('rta_permit_details')} />
        )}
      </div>

      <div className="border-t border-gray-100 pt-4">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input type="checkbox" checked={!!form.visited_uae_before} onChange={set('visited_uae_before')} />
          Did you visit the UAE before?
        </label>
        {form.visited_uae_before && (
          <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
            <Input label="Old visa" placeholder="Old visa no. / type" value={form.old_visa || ''} onChange={set('old_visa')} />
            <Textarea label="Remark" value={form.old_visa_remark || ''} onChange={set('old_visa_remark')} />
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" onClick={() => setEditing(false)}>
          Cancel
        </Button>
        <Button type="submit" loading={mutation.isPending}>
          Save changes
        </Button>
      </div>
    </form>
  );
}
